// "How it works" — 5-slide onboarding modal.
//
// Triggered by the "How it works" nav link on both index.html (landing)
// and app.html. Self-contained: this module owns the markup, the slide
// data, and the open/close lifecycle. Imported by app.js and by
// landing-vaults.js so it ships in both bundles.
//
// UX rules (locked with JP):
//   - Nav link → open. Never auto-opens. Never persists "seen".
//   - 5 slides, simple and intuitive: vault concept → connect → fund →
//     deposit → share price + withdraw.
//   - "Fund your wallet" slide funnels via Jumper as the universal bridge
//     (handles any source chain / token), with HL Portfolio as a
//     secondary path for users already inside Hyperliquid.
//   - Visuals are inline SVG drawn in our cyan/dark palette. No GIFs, no
//     third-party screenshots — survives upstream UI changes.
//   - Page underneath stays put; close X / Esc / backdrop click dismisses.

const JUMPER_URL = "https://jumper.exchange/?toChain=999&toToken=0xb88339CB7199b77E23DB6E890353E22632Ba630f"; // USDC on HyperEVM
const HL_PORTFOLIO_URL = "https://app.hyperliquid.xyz/portfolio";

// Slide 1 visual — three trust pillars (non-custodial / algorithmic / on-chain).
// Slide 1 sets the trust framing only; the share-price growth narrative is
// reserved for slide 5 so its punchline stays intact.
//
// Iteration: dropped the caption text under each pillar (JP "less text, more
// visual") and swapped generic Material icons for custom brand-styled SVG
// glyphs sitting in soft-glow frames. Each pillar = one bold label, one
// distinctive glyph in a distinct brand colour (primary/tertiary/secondary).
const SLIDE_1_HTML = `
<div class="grid grid-cols-3 gap-4 max-w-2xl mx-auto py-2">
  <!-- Pillar 1: Non-custodial — stylised padlock with keyhole -->
  <div class="flex flex-col items-center text-center">
    <div class="w-24 h-24 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center mb-4 shadow-[0_0_36px_rgba(138,235,255,0.18)]">
      <svg viewBox="0 0 64 64" class="w-14 h-14" aria-hidden="true">
        <!-- Shackle -->
        <path d="M 20 30 L 20 22 Q 20 12 32 12 Q 44 12 44 22 L 44 30" fill="none" stroke="#22D3EE" stroke-width="2.5" stroke-linecap="round"/>
        <!-- Body -->
        <rect x="12" y="30" width="40" height="28" rx="5" fill="#22D3EE" fill-opacity="0.12" stroke="#22D3EE" stroke-width="2.5"/>
        <!-- Keyhole -->
        <circle cx="32" cy="42" r="3" fill="#22D3EE"/>
        <line x1="32" y1="42" x2="32" y2="50" stroke="#22D3EE" stroke-width="2.5" stroke-linecap="round"/>
      </svg>
    </div>
    <div class="font-headline font-bold text-sm text-on-surface tracking-wide">Non-custodial</div>
  </div>

  <!-- Pillar 2: Algorithmic — hex chip with an activity wave -->
  <div class="flex flex-col items-center text-center">
    <div class="w-24 h-24 rounded-2xl bg-tertiary/10 border border-tertiary/30 flex items-center justify-center mb-4 shadow-[0_0_36px_rgba(106,242,222,0.18)]">
      <svg viewBox="0 0 64 64" class="w-14 h-14" aria-hidden="true">
        <!-- Chip body (hexagon) -->
        <polygon points="32,8 54,20 54,44 32,56 10,44 10,20" fill="#6af2de" fill-opacity="0.12" stroke="#6af2de" stroke-width="2.5" stroke-linejoin="round"/>
        <!-- Inner activity wave -->
        <polyline points="18,38 24,30 30,34 36,24 42,30 48,22" fill="none" stroke="#6af2de" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        <!-- End node -->
        <circle cx="48" cy="22" r="3" fill="#6af2de"/>
      </svg>
    </div>
    <div class="font-headline font-bold text-sm text-on-surface tracking-wide">Algorithmic</div>
  </div>

  <!-- Pillar 3: On-chain — two interlocked links -->
  <div class="flex flex-col items-center text-center">
    <div class="w-24 h-24 rounded-2xl bg-secondary/10 border border-secondary/30 flex items-center justify-center mb-4 shadow-[0_0_36px_rgba(137,206,255,0.18)]">
      <svg viewBox="0 0 64 64" class="w-14 h-14" aria-hidden="true">
        <!-- Left link (rotated rounded rect) -->
        <rect x="6" y="22" width="32" height="20" rx="10" fill="none" stroke="#89ceff" stroke-width="2.5"/>
        <!-- Right link, overlapping -->
        <rect x="26" y="22" width="32" height="20" rx="10" fill="none" stroke="#89ceff" stroke-width="2.5"/>
        <!-- Highlight at the seam -->
        <line x1="32" y1="28" x2="32" y2="36" stroke="#89ceff" stroke-width="2.5" stroke-linecap="round" stroke-opacity="0.6"/>
      </svg>
    </div>
    <div class="font-headline font-bold text-sm text-on-surface tracking-wide">On-chain</div>
  </div>
</div>`;

// Slide 2 visual — clean wallet card + HyperEVM chain pill.
// We officially support MetaMask only (the rest of the connect flow has
// only been tested there). The wallet card is brand-neutral on purpose:
// the geometric "fox" sketch read as strange (JP feedback), and a clean
// abstract wallet icon paired with the explicit "Install MetaMask" copy
// in the slide body communicates the same thing without the bad art.
const SLIDE_2_SVG = `
<svg viewBox="0 0 320 180" class="w-full max-w-sm mx-auto" aria-hidden="true">
  <!-- Wallet glyph -->
  <g transform="translate(110, 16)">
    <!-- Main wallet body -->
    <rect x="0" y="12" width="100" height="70" rx="8" fill="#1a1f2d" stroke="#22D3EE" stroke-width="1.5"/>
    <!-- Top flap -->
    <path d="M 10 12 L 10 4 Q 10 0 14 0 L 76 0 Q 80 0 80 4 L 80 12" fill="none" stroke="#22D3EE" stroke-width="1.5" stroke-linejoin="round"/>
    <!-- Coin slot on right -->
    <circle cx="82" cy="48" r="11" fill="#22D3EE" fill-opacity="0.15" stroke="#22D3EE" stroke-width="1.2"/>
    <circle cx="82" cy="48" r="4" fill="#22D3EE"/>
    <!-- Address line on left -->
    <rect x="16" y="36" width="48" height="3" rx="1.5" fill="#22D3EE" fill-opacity="0.4"/>
    <rect x="16" y="46" width="36" height="3" rx="1.5" fill="#22D3EE" fill-opacity="0.25"/>
    <rect x="16" y="60" width="28" height="3" rx="1.5" fill="#22D3EE" fill-opacity="0.25"/>
  </g>
  <!-- Connector dots -->
  <g transform="translate(155, 110)">
    <circle cx="0" cy="0" r="1.5" fill="#6af2de" fill-opacity="0.7"/>
    <circle cx="0" cy="6" r="1.5" fill="#6af2de" fill-opacity="0.5"/>
    <circle cx="0" cy="12" r="1.5" fill="#6af2de" fill-opacity="0.3"/>
  </g>
  <!-- Chain pill below -->
  <g transform="translate(75, 140)">
    <rect width="170" height="28" rx="14" fill="#1a1f2d" stroke="#6af2de" stroke-width="1"/>
    <circle cx="18" cy="14" r="3.5" fill="#6af2de"/>
    <text x="32" y="18" font-family="Space Grotesk" font-size="10" font-weight="600" fill="#8aebff" letter-spacing="1">HYPEREVM · CHAIN 999</text>
  </g>
</svg>`;

// Slide 3 visual — two glowing token badges (USDC + HYPE) with a "+" between.
// JP feedback: the earlier wallet-card mockup was confusing — it showed fake
// dollar amounts that read like real data, and restated the body copy in
// pictogram form. Stripped to the minimum: "you need both these tokens", let
// the body copy explain why. Same circular-glow language as slide 1's
// trust-pillar frames so the deck feels cohesive.
const SLIDE_3_HTML = `
<div class="flex items-center justify-center gap-6 md:gap-8 py-4">
  <!-- USDC token badge -->
  <div class="w-28 h-28 rounded-full bg-primary/10 border-2 border-primary/40 flex items-center justify-center shadow-[0_0_40px_rgba(138,235,255,0.2)]">
    <span class="font-headline font-bold text-xl text-primary tracking-tight">USDC</span>
  </div>
  <!-- + connector -->
  <span class="font-headline text-4xl text-on-surface-variant/30 font-light select-none">+</span>
  <!-- HYPE token badge -->
  <div class="w-28 h-28 rounded-full bg-tertiary/10 border-2 border-tertiary/40 flex items-center justify-center shadow-[0_0_40px_rgba(106,242,222,0.2)]">
    <span class="font-headline font-bold text-xl text-tertiary tracking-tight">HYPE</span>
  </div>
</div>`;

// Slide 4 visual — replica of our actual deposit card (mocked numbers).
// Mirrors the deposit.js HTML structure so it feels native.
const SLIDE_4_HTML = `
<div class="max-w-sm mx-auto bg-surface-container-lowest rounded-lg border border-outline-variant/10 p-5">
  <div class="flex items-center gap-2 mb-3">
    <span class="material-symbols-outlined text-primary text-base">arrow_downward</span>
    <span class="font-headline font-bold text-sm text-on-surface">Deposit USDC</span>
  </div>
  <div class="flex justify-between text-[0.65rem] text-secondary/60 mb-1">
    <span>Amount</span>
    <span>Balance: <span class="text-primary">100.00</span> USDC</span>
  </div>
  <div class="flex items-center bg-surface-container-high rounded-lg border border-outline-variant/20 overflow-hidden mb-3">
    <span class="flex-1 px-3 py-2 text-base font-headline text-on-surface">100.00</span>
    <span class="px-3 py-1 mr-2 text-[0.6rem] text-primary border border-primary/30 rounded">MAX</span>
    <span class="pr-3 text-xs text-secondary">USDC</span>
  </div>
  <div class="text-[0.65rem] text-secondary/60 mb-3">
    You will receive: <span class="text-on-surface font-semibold">100.0000</span> shares
  </div>
  <div class="w-full py-2 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold text-sm rounded-DEFAULT text-center">
    Deposit
  </div>
</div>`;

// Slide 5 visual — share price gently climbing from $1.00.
const SLIDE_5_SVG = `
<svg viewBox="0 0 420 160" class="w-full max-w-md mx-auto" aria-hidden="true">
  <defs>
    <linearGradient id="hiw-grad-fill" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#6af2de" stop-opacity="0.25"/>
      <stop offset="100%" stop-color="#6af2de" stop-opacity="0"/>
    </linearGradient>
  </defs>
  <!-- Y-axis labels -->
  <text x="30" y="34" text-anchor="end" font-family="Space Grotesk" font-size="9" fill="#bbc9cd">$1.04</text>
  <text x="30" y="78" text-anchor="end" font-family="Space Grotesk" font-size="9" fill="#bbc9cd">$1.02</text>
  <text x="30" y="122" text-anchor="end" font-family="Space Grotesk" font-size="9" fill="#bbc9cd">$1.00</text>
  <!-- Baseline -->
  <line x1="40" y1="118" x2="400" y2="118" stroke="#3c494c" stroke-width="0.5" stroke-dasharray="3 4"/>
  <!-- Filled area under curve -->
  <path d="M 40 118 Q 100 116 140 112 T 220 96 T 300 74 T 400 38 L 400 118 Z" fill="url(#hiw-grad-fill)"/>
  <!-- Curve -->
  <path d="M 40 118 Q 100 116 140 112 T 220 96 T 300 74 T 400 38" fill="none" stroke="#6af2de" stroke-width="2" stroke-linecap="round"/>
  <!-- End dot -->
  <circle cx="400" cy="38" r="4" fill="#6af2de"/>
  <circle cx="400" cy="38" r="8" fill="#6af2de" fill-opacity="0.25"/>
  <!-- Start label -->
  <text x="40" y="140" text-anchor="middle" font-family="Inter" font-size="8" fill="#bbc9cd" letter-spacing="0.5">START · $1.00</text>
  <!-- End label -->
  <text x="400" y="140" text-anchor="middle" font-family="Inter" font-size="8" fill="#6af2de" letter-spacing="0.5">TODAY · $1.04</text>
</svg>`;

const SLIDES = [
  {
    title: "Algorithmic vaults, on-chain",
    visual: SLIDE_1_HTML,
    body: `Vaults are <span class="text-primary font-semibold">smart contracts on HyperEVM</span>. We never custody your funds. The algorithm trades; your share price reflects the result.`,
    // "$10 minimum" pill removed 2026-05-21 — was a retail tell that
    // contradicted persona.md's 4-6 figure depositor target.
    pills: ["~5 min setup", "<$1 in gas"],
  },
  {
    title: "Connect a wallet",
    visual: SLIDE_2_SVG,
    body: `Install <span class="text-primary font-semibold">MetaMask</span> and click Connect. We'll add the HyperEVM network for you on first connect — no manual config.`,
  },
  {
    title: "Fund your wallet on HyperEVM",
    visual: SLIDE_3_HTML,
    body: `You need <span class="text-primary font-semibold">USDC</span> (your deposit amount) and a tiny bit of <span class="text-tertiary font-semibold">HYPE</span> (~0.01, ~$0.30) for gas — both in your wallet on HyperEVM.`,
    primaryCta: { label: "Bridge in via Jumper", url: JUMPER_URL, icon: "open_in_new" },
    secondaryCta: { label: "Already on Hyperliquid? Open HL Portfolio", url: HL_PORTFOLIO_URL, icon: "open_in_new" },
    footnote: "Jumper takes any token on any chain (ETH, BTC, USDC anywhere) and delivers USDC to HyperEVM in one transaction.",
  },
  {
    title: "Pick a vault and deposit",
    visual: SLIDE_4_HTML,
    body: `Pick a gemstone vault, type the USDC amount, click Deposit. First-time deposits sign <span class="text-primary font-semibold">2 transactions</span> (approve + deposit); subsequent deposits are <span class="text-primary font-semibold">1 transaction</span>.`,
  },
  {
    title: "Watch your share grow",
    visual: SLIDE_5_SVG,
    body: `All vaults start at <span class="text-primary font-semibold">$1.00 per share</span>. Every move from $1.00 is real trading performance — recorded on-chain, in real time. When you deposit, you buy in at the current share price (which already reflects all open trades), so your P&amp;L moves with the vault from that moment forward.`,
    footnote: "20% performance fee is charged only at withdrawal, and only on the profit above your cost basis.",
  },
];

// ── Module state ───────────────────────────────────────────────────────

let _mounted = false;
let _currentSlide = 0;
let _onKeydown = null;

// ── Public API ─────────────────────────────────────────────────────────

/** Open the modal. Lazy-mounts on first call. */
export function openHowItWorks() {
  if (!_mounted) {
    _mount();
    _mounted = true;
  }
  _currentSlide = 0;
  _renderSlide();
  const wrap = document.getElementById("hiw-modal");
  if (wrap) {
    wrap.classList.remove("hidden");
    wrap.classList.add("flex");
    document.body.style.overflow = "hidden";  // freeze background scroll
  }
}

/** Close the modal. */
export function closeHowItWorks() {
  const wrap = document.getElementById("hiw-modal");
  if (wrap) {
    wrap.classList.add("hidden");
    wrap.classList.remove("flex");
    document.body.style.overflow = "";
  }
}

/**
 * Wire the "How it works" nav link(s). Looks for any element with
 * `data-howitworks` attribute on the page and attaches a click handler.
 * Call once after DOM is ready (app.js does this on init; landing-vaults.js
 * calls it after its own render).
 */
export function wireHowItWorksTriggers() {
  document.querySelectorAll("[data-howitworks]").forEach((el) => {
    if (el.dataset._hiwWired) return;  // idempotent
    el.dataset._hiwWired = "1";
    el.addEventListener("click", (e) => {
      e.preventDefault();
      openHowItWorks();
    });
  });
}

// ── Internals ──────────────────────────────────────────────────────────

function _mount() {
  const html = `
<div id="hiw-modal" class="hidden fixed inset-0 z-[110] items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="hiw-title">
  <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" data-hiw-backdrop></div>
  <div class="relative bg-surface-container border border-outline-variant/15 rounded-xl shadow-[0_0_80px_rgba(47,217,244,0.1)] max-w-2xl w-full mx-4 my-8 max-h-[92vh] overflow-y-auto">
    <!-- Header: progress dots + close -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10">
      <div class="flex items-center gap-2" id="hiw-dots"></div>
      <button id="hiw-close" class="text-secondary/60 hover:text-on-surface transition-colors" aria-label="Close">
        <span class="material-symbols-outlined">close</span>
      </button>
    </div>
    <!-- Slide content -->
    <div id="hiw-slide" class="px-6 py-8 min-h-[420px]"></div>
    <!-- Footer: prev / counter / next -->
    <div class="flex items-center justify-between px-6 py-4 border-t border-outline-variant/10">
      <button id="hiw-prev" class="flex items-center gap-1 text-sm text-on-surface-variant hover:text-on-surface transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
        <span class="material-symbols-outlined text-base">arrow_back</span>
        Back
      </button>
      <span id="hiw-counter" class="text-xs text-secondary/50 tabular-nums"></span>
      <button id="hiw-next" class="flex items-center gap-1 px-4 py-2 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold text-sm rounded-DEFAULT hover:shadow-[0_0_24px_rgba(138,235,255,0.4)] transition-all">
        <span id="hiw-next-label">Next</span>
        <span class="material-symbols-outlined text-base">arrow_forward</span>
      </button>
    </div>
  </div>
</div>`;
  const div = document.createElement("div");
  div.innerHTML = html;
  document.body.appendChild(div.firstElementChild);

  // Wiring
  document.getElementById("hiw-close").addEventListener("click", closeHowItWorks);
  document.querySelector("[data-hiw-backdrop]").addEventListener("click", closeHowItWorks);
  document.getElementById("hiw-prev").addEventListener("click", () => _goto(_currentSlide - 1));
  document.getElementById("hiw-next").addEventListener("click", () => {
    if (_currentSlide === SLIDES.length - 1) {
      // Last slide: "Get started" closes the modal and routes to deposit.
      closeHowItWorks();
      // Same-page hash change on app.html triggers the router; on
      // index.html it sends them to the app.
      const onApp = location.pathname.endsWith("/app.html") || location.pathname === "/app";
      location.href = onApp ? "#deposit" : "app.html#deposit";
    } else {
      _goto(_currentSlide + 1);
    }
  });

  // Esc + arrow keys (only when modal is open)
  _onKeydown = (e) => {
    const wrap = document.getElementById("hiw-modal");
    if (!wrap || wrap.classList.contains("hidden")) return;
    if (e.key === "Escape") { closeHowItWorks(); }
    else if (e.key === "ArrowLeft" && _currentSlide > 0) { _goto(_currentSlide - 1); }
    else if (e.key === "ArrowRight" && _currentSlide < SLIDES.length - 1) { _goto(_currentSlide + 1); }
  };
  document.addEventListener("keydown", _onKeydown);
}

function _goto(idx) {
  if (idx < 0 || idx >= SLIDES.length) return;
  _currentSlide = idx;
  _renderSlide();
}

function _renderSlide() {
  const s = SLIDES[_currentSlide];
  const slideEl = document.getElementById("hiw-slide");
  if (!slideEl) return;

  // Slide body
  const pillsHtml = s.pills
    ? `<div class="flex flex-wrap items-center justify-center gap-2 mt-5">
         ${s.pills.map(p => `<span class="px-3 py-1 text-[0.65rem] uppercase tracking-widest bg-surface-container-low border border-outline-variant/15 rounded-full text-on-surface-variant">${p}</span>`).join("")}
       </div>`
    : "";

  const ctasHtml = [];
  if (s.primaryCta) {
    ctasHtml.push(`<a href="${s.primaryCta.url}" target="_blank" rel="noopener" class="inline-flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold text-sm rounded-DEFAULT hover:shadow-[0_0_24px_rgba(138,235,255,0.4)] transition-all">
      ${s.primaryCta.label}
      <span class="material-symbols-outlined text-base">${s.primaryCta.icon || "arrow_forward"}</span>
    </a>`);
  }
  if (s.secondaryCta) {
    ctasHtml.push(`<a href="${s.secondaryCta.url}" target="_blank" rel="noopener" class="inline-flex items-center justify-center gap-2 w-full py-2.5 bg-surface-container-high border border-outline-variant/20 text-on-surface font-headline text-sm rounded-DEFAULT hover:border-primary/40 transition-all">
      ${s.secondaryCta.label}
      <span class="material-symbols-outlined text-base">${s.secondaryCta.icon || "open_in_new"}</span>
    </a>`);
  }

  const footnoteHtml = s.footnote
    ? `<p class="text-xs text-secondary/60 text-center mt-4 leading-relaxed">${s.footnote}</p>`
    : "";

  slideEl.innerHTML = `
    <div class="mb-6">${s.visual}</div>
    <h3 id="hiw-title" class="font-headline text-2xl font-bold text-on-surface text-center mb-3">${s.title}</h3>
    <p class="text-sm md:text-base text-on-surface-variant text-center leading-relaxed max-w-lg mx-auto">${s.body}</p>
    ${pillsHtml}
    ${ctasHtml.length ? `<div class="mt-6 max-w-sm mx-auto space-y-2">${ctasHtml.join("")}</div>` : ""}
    ${footnoteHtml}
  `;

  // Progress dots
  const dotsEl = document.getElementById("hiw-dots");
  if (dotsEl) {
    dotsEl.innerHTML = SLIDES.map((_, i) => {
      const active = i === _currentSlide;
      const cls = active
        ? "w-6 h-1.5 rounded-full bg-primary transition-all"
        : "w-1.5 h-1.5 rounded-full bg-outline-variant/40 hover:bg-outline-variant transition-all cursor-pointer";
      return `<button data-hiw-dot="${i}" class="${cls}" aria-label="Go to slide ${i+1}"></button>`;
    }).join("");
    dotsEl.querySelectorAll("[data-hiw-dot]").forEach((btn) => {
      btn.addEventListener("click", () => _goto(parseInt(btn.dataset.hiwDot, 10)));
    });
  }

  // Counter
  const counterEl = document.getElementById("hiw-counter");
  if (counterEl) counterEl.textContent = `${_currentSlide + 1} / ${SLIDES.length}`;

  // Prev / next buttons
  const prevBtn = document.getElementById("hiw-prev");
  if (prevBtn) prevBtn.disabled = _currentSlide === 0;
  const nextLabel = document.getElementById("hiw-next-label");
  if (nextLabel) nextLabel.textContent = _currentSlide === SLIDES.length - 1 ? "Get started" : "Next";
}
